import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SlidePanel from '../shared/SlidePanel';
import ServiceEditorPanel from './ServiceEditorPanel';
import CopyLinkButton from '../shared/shareLinks';
import { markFirstTask } from '../onboarding/firstTaskRegistry';
import { newServicesAction } from '../../_actions';
import '../shared/ui.css';
import './ServiceDetailPanel.css';

/**
 * ServiceDetailPanel — read-only service summary in a SlidePanel.
 * Opens when tapping a service in the list. Shows key info at a
 * glance with staff pricing, deposit info, and edit/delete actions.
 */
const ServiceDetailPanel = ({ service, onClose, onDelete, onEdit, onSaved, currency = '£' }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const slidePanelRef = useRef(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);

    const serviceId = service?.originalId || service?.id;

    useEffect(() => {
        if (!serviceId) return;
        markFirstTask('price');
        setLoading(true);
        dispatch(newServicesAction.viewServiceData({ commonServiceId: serviceId }))
            .then((res) => {
                // The action returns { viewService: { ...data } }
                const data = res?.viewService || res?.viewServiceData || res;
                console.log('[ServiceDetailPanel] viewService response:', data);
                setDetail(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [serviceId, dispatch]);

    const handleEdit = () => {
        setEditing(true);
    };

    const handleBackFromEdit = () => {
        setEditing(false);
        // Re-fetch detail to show updated data
        if (serviceId) {
            setLoading(true);
            dispatch(newServicesAction.viewServiceData({ commonServiceId: serviceId }))
                .then((res) => {
                    const data = res?.viewService || res?.viewServiceData || res;
                    setDetail(data);
                    setLoading(false);
                })
                .catch(() => setLoading(false));
        }
    };

    const handleDelete = () => {
        if (onDelete) onDelete(serviceId, service?.name);
    };

    // Computed fields from detail or summary — field names match the
    // raw API response shape (serviceName, serviceDescription,
    // hairdresserService, etc.)
    const name = detail?.serviceName || detail?.name || service?.name || 'Service';
    const categoryName = detail?.categoryId?.categoryName || detail?.category?.categoryName || service?.category || '';
    const description = detail?.serviceDescription || detail?.description || service?.description || '';
    const diaryColour = detail?.defaultDiaryColour || detail?.diaryColour || '#29282D';
    const images = detail?.serviceImages || [];
    const staffSettings = detail?.hairdresserService || detail?.staffSettings || [];
    const linkedForms = detail?.linkedForms || [];

    // Compute price and duration ranges from staff settings
    const { priceDisplay, durationDisplay, depositDisplay } = useMemo(() => {
        const staffPrices = staffSettings.map(s => Number(s.servicePrice || 0)).filter(p => p > 0);
        const staffDurations = staffSettings.map(s => Number(s.serviceTime || 0)).filter(d => d > 0);
        const staffDeposits = staffSettings.map(s => Number(s.depositPrice || 0)).filter(d => d > 0);

        // Fallback to service-level if no staff data
        const fallbackPrice = Number(detail?.servicePrice || service?.price || 0);
        const fallbackDuration = Number(detail?.serviceTime || service?.duration || 0);
        const fallbackDeposit = Number(detail?.depositPrice || service?.deposit || 0);

        let priceDisplay;
        if (staffPrices.length > 0) {
            const min = Math.min(...staffPrices);
            const max = Math.max(...staffPrices);
            priceDisplay = min === max
                ? `${currency}${min.toFixed(2)}`
                : `${currency}${min.toFixed(2)} - ${currency}${max.toFixed(2)}`;
        } else {
            priceDisplay = `${currency}${fallbackPrice.toFixed(2)}`;
        }

        let durationDisplay;
        if (staffDurations.length > 0) {
            const min = Math.min(...staffDurations);
            const max = Math.max(...staffDurations);
            durationDisplay = min === max ? `${min} min` : `${min} - ${max} min`;
        } else {
            durationDisplay = `${fallbackDuration} min`;
        }

        let depositDisplay = null;
        if (staffDeposits.length > 0) {
            const min = Math.min(...staffDeposits);
            const max = Math.max(...staffDeposits);
            depositDisplay = min === max
                ? `${currency}${min.toFixed(2)}`
                : `${currency}${min.toFixed(2)} - ${currency}${max.toFixed(2)}`;
        } else if (fallbackDeposit > 0) {
            depositDisplay = `${currency}${fallbackDeposit.toFixed(2)}`;
        }

        return { priceDisplay, durationDisplay, depositDisplay };
    }, [detail, service, staffSettings, currency]);

    return (
        <SlidePanel
            ref={slidePanelRef}
            onClose={onClose}
            className={`sdp-panel ${editing ? 'sdp-panel--editing' : ''}`}
            width={editing ? 700 : 420}
        >
            {editing ? (
                <ServiceEditorPanel
                    serviceId={serviceId}
                    inline
                    onBack={handleBackFromEdit}
                    onClose={onClose}
                    onSaved={() => {
                        if (onSaved) onSaved();
                        if (window.innerWidth < 768) {
                            onClose();
                        } else {
                            handleBackFromEdit();
                        }
                    }}
                />
            ) : (
            <>
            {/* Header */}
            <div className="sdp-header">
                <div className="sdp-header-info">
                    <h2 className="sdp-header-name">{name}</h2>
                    {categoryName && <span className="sdp-header-category">{categoryName}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CopyLinkButton type="service" id={serviceId} title="Copy service link" />
                    <button className="beu-icon-btn" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Scrollable body */}
            <div className="sdp-body">
                {/* Price + Duration summary box */}
                <div className="sdp-stats-row">
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{priceDisplay}</span>
                        <span className="sdp-stat-label">Price</span>
                    </div>
                    <div className="sdp-stat-divider" />
                    <div className="sdp-stat">
                        <span className="sdp-stat-value">{durationDisplay}</span>
                        <span className="sdp-stat-label">Duration</span>
                    </div>
                </div>

                {/* Description */}
                {description && (
                    <div className="sdp-section">
                        <p className="beu-section-label">Description</p>
                        <p className="sdp-description">{description}</p>
                    </div>
                )}

                {/* Service images — mini strip under description */}
                {images.length > 0 && (
                    <div className="sdp-image-strip">
                        {images.slice(0, 4).map((img, idx) => (
                            <div key={idx} className="sdp-image-thumb">
                                <img src={img?.url || img} alt="" />
                            </div>
                        ))}
                        {images.length > 4 && (
                            <div className="sdp-image-thumb sdp-image-thumb--more">+{images.length - 4}</div>
                        )}
                    </div>
                )}

                {/* Staff pricing */}
                {loading ? (
                    <div className="sdp-section">
                        <p className="beu-section-label">Staff pricing</p>
                        <div className="sdp-shimmer" />
                        <div className="sdp-shimmer sdp-shimmer--short" />
                        <div className="sdp-shimmer sdp-shimmer--shorter" />
                    </div>
                ) : staffSettings.length > 0 ? (
                    <div className="sdp-section">
                        <div className="sdp-section-header">
                            <p className="beu-section-label">Staff pricing</p>
                            <span className="sdp-staff-count">{staffSettings.length} staff</span>
                        </div>
                        <div className="sdp-staff-list">
                            {staffSettings.map((staff, idx) => {
                            const staffName = staff.name || staff.hairdresserName || 'Staff';
                            const staffPrice = Number(staff.servicePrice || 0);
                            const staffTime = Number(staff.serviceTime || 0);
                            const staffDeposit = Number(staff.depositPrice || 0);
                            const staffColour = staff.diaryColour || diaryColour;
                            return (
                                <div key={idx} className="sdp-staff-row">
                                    <span className="sdp-staff-dot" style={{ background: staffColour }} />
                                    <span className="sdp-staff-name">{staffName}</span>
                                    <span className="sdp-staff-meta">{staffTime}m</span>
                                    <span className="sdp-staff-price">{currency}{staffPrice.toFixed(2)}</span>
                                    {staffDeposit > 0 && (
                                        <span className="sdp-staff-deposit">{currency}{staffDeposit.toFixed(2)} deposit</span>
                                    )}
                                </div>
                            );
                        })}
                        </div>
                    </div>
                ) : null}

                {/* Linked forms */}
                {!loading && linkedForms.length > 0 && (
                    <div className="sdp-section">
                        <p className="beu-section-label">Linked forms</p>
                        <div className="sdp-forms-list">
                            {linkedForms.map((form, idx) => (
                                <div key={idx} className="sdp-form-row">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    <span className="sdp-form-name">{form.formName || form.name || 'Form'}</span>
                                    {form.sendTo && <span className="sdp-form-badge">{form.sendTo === 'all' ? 'Auto-send' : 'Manual'}</span>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer — side by side buttons matching diary pattern */}
            <div className="sdp-footer">
                <button type="button" className="sdp-btn sdp-btn--secondary" onClick={handleDelete}>
                    Delete
                </button>
                <button type="button" className="sdp-btn sdp-btn--primary" onClick={handleEdit}>
                    Edit service
                </button>
            </div>
            </>
            )}
        </SlidePanel>
    );
};

export default ServiceDetailPanel;
